import type { ScaleDetailRecord } from '../types/sidecar';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const scaleDetailQueryAtom = createQueryAtomFactory(
  'settings.scaleDetail',
  (params: { id: string }) => ({
    scale: {
      __args: { id: params.id },
      id: true,
      sidecarId: true,
      name: true,
      connectionType: true,
      serialPortPath: true,
      baudRate: true,
      dataBits: true,
      stopBits: true,
      parity: true,
      tcpHost: true,
      tcpPort: true,
      scaleFormatType: true,
      measurementMode: true,
      onDemandString: true,
      unitOfMeasurement: true,
      measurementIncrement: true,
      cameraId: true,
      status: true,
      online: true,
      lastSeenAt: true,
      camera: {
        id: true,
        name: true,
        sidecarId: true,
      },
      sidecar: {
        id: true,
        name: true,
        facilityId: true,
        peerId: true,
        multiaddrs: true,
        status: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): ScaleDetailRecord | null => {
      const scale = data?.scale;
      if (!scale) {
        return null;
      }

      return {
        id: scale.id,
        sidecarId: scale.sidecarId,
        cameraId: scale.cameraId ?? null,
        name: scale.name,
        connectionType: scale.connectionType,
        serialPortPath: scale.serialPortPath ?? null,
        baudRate: scale.baudRate ?? null,
        dataBits: scale.dataBits ?? null,
        stopBits: scale.stopBits ?? null,
        parity: scale.parity ?? null,
        tcpHost: scale.tcpHost ?? null,
        tcpPort: scale.tcpPort ?? null,
        scaleFormatType: scale.scaleFormatType,
        measurementMode: scale.measurementMode,
        onDemandString: scale.onDemandString ?? null,
        unitOfMeasurement: scale.unitOfMeasurement ?? null,
        measurementIncrement: scale.measurementIncrement ?? null,
        status: scale.status,
        online: scale.online ?? false,
        lastSeenAt: scale.lastSeenAt
          ? parseZonedDateTime(scale.lastSeenAt)
          : null,
        currentGrossWeight: null,
        currentTareWeight: null,
        currentNetWeight: null,
        camera: scale.camera
          ? {
              id: scale.camera.id,
              name: scale.camera.name,
              sidecarId: scale.camera.sidecarId,
            }
          : null,
        sidecar: scale.sidecar
          ? { id: scale.sidecar.id, name: scale.sidecar.name }
          : null,
        sidecarDetail: scale.sidecar
          ? {
              id: scale.sidecar.id,
              name: scale.sidecar.name,
              facilityId: scale.sidecar.facilityId ?? null,
              peerId: scale.sidecar.peerId ?? null,
              multiaddrs: (scale.sidecar.multiaddrs as string[]) ?? [],
              status: scale.sidecar.status,
            }
          : null,
        timestamps: {
          createdAt: parseZonedDateTime(scale.timestamps.createdAt),
          updatedAt: parseZonedDateTime(scale.timestamps.updatedAt),
        },
      };
    },
  },
);
